import { MMOL_TO_MGDL_FACTOR } from './utils/glucoseUnits'

export const GLUCOSE_UNIT_MG = 'mg/dL'
export const GLUCOSE_UNIT_MM = 'mmol/L'
export const GLUCOSE_UNIT_DEFAULT = GLUCOSE_UNIT_MM
export const GLUCOSE_VALID_RANGE = { min: 2.2, max: 40 }
// Out-of-sensor-range high threshold (mirrors AdapetApp). 30 mmol/L pairs with
// 540 mg/dL by the clinical rounding convention; it is the chart's Y-axis cap.
export const GLUCOSE_OUT_OF_RANGE_HIGH = 30 // mmol/L (540 mg/dL)
export const GLUCOSE_OUT_OF_RANGE_HIGH_MG = 540
// Upper clip for the DISPLAY pipeline, 10 mmol above the valid-range max. The
// BGM correction compresses highs (bgm(40)=27.7 mmol, below the cap), so a raw
// clamp at 40 would keep the 540 cap unreachable; clamping at 50 maps a
// saturated reading to bgm(50)≈31 mmol — past the threshold — to reach the cap.
export const GLUCOSE_DISPLAY_CLAMP_MAX = GLUCOSE_VALID_RANGE.max + 10 // 50
export const GLUCOSE_FIXED_AXIS_MGDL_FACTOR = 18
export const GLUCOSE_FIXED_AXIS = {
  min: 0,
  // Y-axis caps at the out-of-range threshold (30 mmol/L = 540 mg/dL), matching
  // AdapetApp. Out-of-range data is clamped to this ceiling.
  max: GLUCOSE_OUT_OF_RANGE_HIGH,
  tickStep: 10,
}
export const GLUCOSE_FIXED_AXIS_TICKS_MMOL = Array.from(
  { length: Math.floor(GLUCOSE_FIXED_AXIS.max / GLUCOSE_FIXED_AXIS.tickStep) + 1 },
  (_, index) => index * GLUCOSE_FIXED_AXIS.tickStep,
)

export function getFixedGlucoseAxis(unit: string = GLUCOSE_UNIT_MM) {
  const tickValues =
    unit === GLUCOSE_UNIT_MG
      ? GLUCOSE_FIXED_AXIS_TICKS_MMOL.map((value) => value * GLUCOSE_FIXED_AXIS_MGDL_FACTOR)
      : GLUCOSE_FIXED_AXIS_TICKS_MMOL

  return {
    min: GLUCOSE_FIXED_AXIS.min,
    max: tickValues[tickValues.length - 1],
    tickValues,
    tickLabels: tickValues.map(String),
  }
}

// Grey used for line stretches pinned to a display bound (out-of-range high above
// the axis ceiling, or sensor-floor low). Centralized here so every glucose chart
// caps with the same color. (Re-exported from GlucoseTrendChart for back-compat.)
export const GLUCOSE_CAP_COLOR = '#9e9e9e'

// Low out-of-range threshold in mg/dL, derived from the single source of truth
// (GLUCOSE_VALID_RANGE.min) so mmol/L and mg/dL stay consistent. 2.2 mmol/L → 40.
export const GLUCOSE_OUT_OF_RANGE_LOW = GLUCOSE_VALID_RANGE.min // 2.2 mmol/L
export const GLUCOSE_OUT_OF_RANGE_LOW_MG = Math.round(GLUCOSE_VALID_RANGE.min * MMOL_TO_MGDL_FACTOR)

/**
 * True when a glucose value (in mmol/L) is outside the sensor display range and
 * should be masked as "Out of range" rather than shown as a number.
 *   high: > GLUCOSE_OUT_OF_RANGE_HIGH (30 mmol/L / 540 mg/dL)
 *   low:  < GLUCOSE_VALID_RANGE.min   (2.2 mmol/L / 40 mg/dL)
 */
export function isGlucoseOutOfRange(mmolValue: number): boolean {
  // NOTE the asymmetry — it is deliberate and load-bearing (L-035/L-046).
  //
  // The low test is `<=`, not `<`. `clampToDisplayRange` FLOORS to exactly
  // `GLUCOSE_VALID_RANGE.min`, and `GLUCOSE_OUT_OF_RANGE_LOW` IS that same
  // constant. Every value that reaches a mask has been through the clamp
  // (normalizeGlucoseReadingForChart → smoothCGM is the pipeline AGENTS.md
  // mandates), so with a strict `<` the low branch is DEAD: a sensor drop-out
  // renders as a confident "40 mg/dL" that a vet cannot tell from a real 40.
  // Measured reach: 36,624 readings below 2.2 mmol/L in 90 days.
  //
  // Cost of `<=`: a genuine reading of exactly 2.2 displays as "<40" rather
  // than "40". That is the sensor's validated FLOOR — a reading sitting exactly
  // on it is indistinguishable from a clamped one, so erring to "<40" is the
  // conservative call, and after BGM correction an exact 2.2 is measure-zero.
  //
  // The high side stays STRICT: the clamp maps >30 into (30,40], never onto 30,
  // so `>` is already reachable there. Do not "tidy" the two into matching.
  return mmolValue > GLUCOSE_OUT_OF_RANGE_HIGH || mmolValue <= GLUCOSE_OUT_OF_RANGE_LOW
}

/**
 * SINGLE SOURCE OF TRUTH for displaying a glucose value (in mmol/L) across all
 * surfaces (chart tooltips, Daily Peak/Nadir/Avg, etc.) so they never disagree.
 *
 * - value > 30 mmol/L (540 mg/dL)  → ">30 mmol/L" (or ">540 mg/dL")
 * - value < 2.2 mmol/L (40 mg/dL)  → "<2.2 mmol/L" (or "<40 mg/dL")
 * - otherwise                      → the formatted value with unit
 *
 * Bounds use the constants (not the value), so "30.9" never leaks. The unit
 * suffix can be dropped with { withUnit: false }.
 */
export function formatGlucoseForDisplay(
  mmolValue: number,
  unit: string = GLUCOSE_UNIT_MM,
  opts: { withUnit?: boolean } = {},
): string {
  const { withUnit = true } = opts
  const isMg = unit === GLUCOSE_UNIT_MG
  const suffix = withUnit ? ` ${unit}` : ''

  // Persisted store values can arrive as garbage (a literal "NaN" string,
  // null, etc. — RaStore keys written by older builds). `.toFixed` on those
  // throws, and one throw takes down the whole pet detail panel behind its
  // error boundary (PROD 2026-07-10, pet 68238: page empty below Alerts).
  // The SSOT formatter must never throw on bad input.
  if (!Number.isFinite(mmolValue)) return `—${suffix}`

  if (mmolValue > GLUCOSE_OUT_OF_RANGE_HIGH) {
    const bound = isMg ? GLUCOSE_OUT_OF_RANGE_HIGH_MG : GLUCOSE_OUT_OF_RANGE_HIGH
    return `>${bound}${suffix}`
  }
  // `<=` — must stay in lockstep with isGlucoseOutOfRange above, or a value
  // masks in one place and prints a number in the other. See the asymmetry
  // note there for why the low bound is inclusive and the high bound is not.
  if (mmolValue <= GLUCOSE_OUT_OF_RANGE_LOW) {
    const bound = isMg ? GLUCOSE_OUT_OF_RANGE_LOW_MG : GLUCOSE_OUT_OF_RANGE_LOW
    return `<${bound}${suffix}`
  }
  const value = isMg ? Math.round(mmolValue * MMOL_TO_MGDL_FACTOR).toString() : mmolValue.toFixed(1)
  return `${value}${suffix}`
}

/**
 * Sanitize a persisted glucose target-range bound read from the RaStore.
 *
 * The per-pet override keys (pet.<id>.targetRangeLow/High) are localStorage
 * values that older builds wrote unvalidated — real browsers in the field hold
 * literal "NaN" strings and nulls. Feeding those to chart math / .toFixed
 * crashed the entire pet detail panel (PROD 2026-07-10, pet 68238). Any value
 * that isn't a positive finite number falls back to the species default.
 */
export function sanitizeGlucoseBound(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

export interface GlucoseLineSegment {
  x: Date[]
  /** y clamped into [displayFloor, displayCeil] — what is plotted */
  y: number[]
  /** original (unclamped) y — used for hover text so "Out of range" survives */
  rawY: number[]
  color: string
}

/**
 * Split a glucose series into colored line segments.
 *
 * Values above `displayCeil` (out-of-range high) or at/below `displayFloor`
 * (sensor-floor low) would otherwise plot off a fixed axis and leave a GAP in
 * the line. Instead we clamp the plotted value to the boundary and color that
 * stretch grey, so the line rides the cap continuously. On-scale points keep the
 * red (out-of-target) / black (in-target) coloring — or a single `baseColor` when
 * provided. A new segment is started on any color change, an over-threshold time
 * gap, or a non-finite reading.
 *
 * Single source of truth shared by every glucose line chart (trend, AGP median,
 * calendar, PDF day charts) so out-of-range rendering never diverges.
 */
export const DEFAULT_GAP_THRESHOLD_MS = 15 * 60 * 1000

/**
 * Adaptive time-gap threshold (ms) for breaking a glucose line across missing
 * data. Based on 1.5× the median sampling interval (floored at 60s), so a real
 * gap breaks the line while normal jitter does not — and the threshold scales
 * up for zoomed/aggregated windows with wider spacing. Single source of truth
 * shared by every glucose line chart so gap rendering never diverges.
 */
export const calculateAdaptiveGapThresholdMs = (timestamps: Date[]): number => {
  if (timestamps.length < 2) return DEFAULT_GAP_THRESHOLD_MS

  const deltas: number[] = []
  for (let i = 1; i < timestamps.length; i++) {
    const delta = timestamps[i].getTime() - timestamps[i - 1].getTime()
    if (delta > 0) deltas.push(delta)
  }

  if (deltas.length === 0) return DEFAULT_GAP_THRESHOLD_MS

  deltas.sort((a, b) => a - b)
  const medianDelta = deltas[Math.floor(deltas.length / 2)]
  return Math.max(60 * 1000, medianDelta * 1.5)
}

export function buildGlucoseLineSegments(
  x: Date[],
  y: number[],
  opts: {
    lowThreshold?: number
    highThreshold?: number
    displayFloor: number
    displayCeil: number
    gapThresholdMs: number
    // When set, in-range stretches use this single color instead of the default
    // red (out-of-target) / black (in-target) coloring. Out-of-range stretches are
    // ALWAYS grey-capped. Lets solid-color line charts (calendar, PDF day charts,
    // alerts) reuse the same cap+clamp logic without adopting target coloring.
    baseColor?: string
  },
): GlucoseLineSegment[] {
  const { lowThreshold, highThreshold, displayFloor, displayCeil, gapThresholdMs, baseColor } = opts
  const segments: GlucoseLineSegment[] = []

  let segX: Date[] = []
  let segY: number[] = []
  let segRawY: number[] = []
  let color = baseColor ?? 'black'

  const flush = () => {
    if (segX.length > 0) {
      segments.push({ x: segX, y: segY, rawY: segRawY, color })
      segX = []
      segY = []
      segRawY = []
    }
  }

  const clampToAxis = (v: number) => Math.min(Math.max(v, displayFloor), displayCeil)
  const colorFor = (v: number) => {
    if (v > displayCeil || v <= displayFloor) return GLUCOSE_CAP_COLOR
    if (baseColor) return baseColor
    return v < (lowThreshold ?? -Infinity) || v > (highThreshold ?? Infinity) ? 'red' : 'black'
  }

  for (let i = 0; i < x.length; i++) {
    const v = y[i]
    if (v === null || v === undefined || !Number.isFinite(v)) {
      flush()
      continue
    }

    if (segX.length > 0) {
      const prevTime = segX[segX.length - 1].getTime()
      const currTime = x[i].getTime()
      if (currTime - prevTime > gapThresholdMs) flush()
    }

    const plotted = clampToAxis(v)
    const newColor = colorFor(v)

    if (newColor !== color && segX.length > 0) {
      // Carry the boundary point into both segments so the color change has no gap.
      segX.push(x[i])
      segY.push(plotted)
      segRawY.push(v)
      flush()
      segX = [x[i]]
      segY = [plotted]
      segRawY = [v]
      color = newColor
    } else {
      segX.push(x[i])
      segY.push(plotted)
      segRawY.push(v)
      if (segX.length === 1) color = newColor
    }
  }
  flush()
  return segments
}

/**
 * Plotly trace shape for ONE segment produced by buildGlucoseLineSegments.
 *
 * A one-point `mode: 'lines'` trace draws NOTHING. buildGlucoseLineSegments
 * carries a colour-transition point into the segment it LEAVES and starts the
 * next segment with that same point, so whenever the LAST reading changes band
 * the new segment is a singleton — and the newest reading, the one a vet reads
 * first, silently vanishes while the visible line ends in the previous band's
 * colour (pet A0004BV / device 537804, 2026-08-26). A lone reading between two
 * time gaps disappears the same way.
 *
 * This lives beside the segment builder on purpose. The fix shipped 2026-08-26
 * (df6191f7) was written inline in GlucoseTrendChart, and the three sibling
 * consumers of the same builder did not get it — so the identical defect stayed
 * live on the Devices "Glucose Trend" card (GlucoseLineChart) and the calendar's
 * monthly overview. Whoever produces the segments owes the caller this decision;
 * a fourth consumer now gets it for free.
 *
 * Segments with >= 2 points return `{ mode: 'lines' }`, byte-identical to the
 * previous behavior, so no normal curve gains dots.
 */
export function glucoseSegmentTraceShape(
  pointCount: number,
  color: string,
  markerSize = 5,
): { mode: 'lines' | 'lines+markers'; marker?: { color: string; size: number } } {
  return pointCount === 1
    ? { mode: 'lines+markers', marker: { color, size: markerSize } }
    : { mode: 'lines' }
}

/**
 * Back-convert a chart/display-unit glucose value to mmol/L for the
 * single-source mask/formatter (isGlucoseOutOfRange / formatGlucoseForDisplay).
 *
 * NOT simply v / factor. mg/dL display values are INTEGER-rounded
 * (mmolToMgdl), so the clamped sensor floor 2.2 mmol/L renders as 40 mg/dL —
 * and 40 / 18.0182 = 2.21998, which slips past the inclusive <= 2.2 low-bound
 * test. The identical drop-out that masked as "<2.2 mmol/L" in mmol mode
 * printed a confident "40 mg/dL" in mg/dL mode (the US default): the mask was
 * unit-dependent. Snapping a display value at/below the display-unit bound
 * onto the mmol bound makes it unit-independent. (The high mask survives the
 * round-trip on its own: >30 mmol rounds to >=541 mg, still >30 coming back.)
 */
export function displayGlucoseToMmol(displayValue: number, unit: string): number {
  if (unit !== GLUCOSE_UNIT_MG) return displayValue
  const mmol = displayValue / MMOL_TO_MGDL_FACTOR
  if (displayValue <= GLUCOSE_OUT_OF_RANGE_LOW_MG && mmol > GLUCOSE_OUT_OF_RANGE_LOW) {
    return GLUCOSE_OUT_OF_RANGE_LOW
  }
  return mmol
}

/**
 * Build masked per-point hover text for a glucose line, in the display unit.
 * `displayValues` are in the active unit; out-of-range points (decided in mmol/L
 * via isGlucoseOutOfRange) render as "<prefix>Out of range (>30 mmol/L)" so the
 * exact value never leaks. `outOfRangeLabel` is the translated "Out of range".
 */
export function buildGlucoseHoverText(
  displayValues: (number | null | undefined)[],
  unit: string,
  outOfRangeLabel: string,
  prefixes?: (string | null)[],
): string[] {
  const toMmol = (v: number) => displayGlucoseToMmol(v, unit)
  return displayValues.map((v, i) => {
    const label = prefixes?.[i] ?? ''
    // For a gap (null/NaN) show just the label (or empty) — no dangling separator.
    if (v === null || v === undefined || !Number.isFinite(v)) return label
    const prefix = label ? `${label} | ` : ''
    const mmol = toMmol(v as number)
    return isGlucoseOutOfRange(mmol)
      ? `${prefix}${outOfRangeLabel} (${formatGlucoseForDisplay(mmol, unit)})`
      : `${prefix}${formatGlucoseForDisplay(mmol, unit)}`
  })
}

/**
 * Device warm-up period in seconds (1 hour = 3600 seconds)
 * After a CGM device is activated, there is a mandatory warm-up period
 * during which glucose data is not clinically valid and should not be
 * displayed or included in analytics.
 */
export const DEVICE_WARMUP_PERIOD_SECONDS = 3600 // 1 hour

/**
 * Device data window period in days (14 days)
 * CGM data should only be displayed for 14 days from device activation.
 * This is the clinically recommended monitoring period.
 */
export const DEVICE_DATA_WINDOW_DAYS = 14

/**
 * Dislodgement detection configuration
 * When consecutive readings show minimum glucose value (indicating sensor not in contact),
 * the data should be filtered out starting from the first dislodgement reading.
 */
export const DISLODGEMENT_DETECTION = {
  // Sliding window size for dislodgement detection
  // 60 readings = ~3 hours of 3-minute readings
  windowSize: 60,
  // Minimum fraction of readings in the window that must be at-minimum to trigger
  // 0.9 = 90% (allows up to 6 noisy readings in 60 to still detect dislodgement)
  windowMinFraction: 0.9,
  // Slack around the FALLBACK minimum only (see sensorFloorThreshold). It is
  // deliberately NOT applied on top of a per-device calibration bound: the
  // stored distribution from 3.0 to 4.0 mmol/L is flat and continuous, so any
  // slack there swallows genuine severe hypoglycemia.
  minimumGlucoseTolerance: 0.5, // Values within 0.5 mmol/L of GLUCOSE_VALID_RANGE.min are considered minimum
  // Recovery thresholds: if glucose returns to a healthy level for this many
  // consecutive readings after a dislodgement trigger, resume showing data
  // from the recovery point onwards. Without this, a single low-glucose run
  // would permanently hide all later valid data — including readings from
  // entirely-fine days after the sensor recovers. UFL trial (Manola, May 16
  // 2026) hit this: 274 consecutive ≤2.7 mmol/L readings hid two days of
  // perfectly normal post-recovery data.
  recoveryGlucoseThreshold: 4.0, // mmol/L — clearly above any clipping artifact
  recoveryReadingsThreshold: 10, // ~30 min of 3-min readings — long enough to rule out a single noisy normal value
  // Bump when any threshold above changes. Surfaced in debug logs so a hidden
  // range in production can be tied back to the exact algorithm that produced it.
  algoVersion: '2026-08-26.1',
}

/**
 * DLS device name shape: exactly 7 alphanumeric characters, no separator.
 * Mirrors ada-core's `DLS_DEVICE_NAME_PATTERN`
 * (src/lib/deviceType.ts#isDlsDeviceName), itself sourced from the mobile
 * app's runtime classifier (AdapetApp/src/services/device/DeviceTypeDetector.js).
 * DX ("DX-") and HUB ("HUB-") names both carry a hyphen, so the absence of one
 * is what distinguishes a DLS ("V Plus") sensor.
 */
export const DLS_DEVICE_NAME_PATTERN = /^[A-Z0-9]{7}$/i

export function isDlsDeviceName(name: string | null | undefined): boolean {
  return typeof name === 'string' && DLS_DEVICE_NAME_PATTERN.test(name)
}

/**
 * DLS sensor wear life (14 days) plus the 6h grace period past it, in seconds.
 * Mirrors AdapetApp `getMaxDlsSessionAgeSeconds` (src/utils/dlsStartTime.js)
 * and ada-core `DLS_SENSOR_EXPIRY_SECONDS`
 * (src/collections/Metrics/sensorExpiry.ts, vic/ada-core#293) exactly:
 * 14 * 24 * 60 * 60 + 6 * 60 * 60 = 1,231,200.
 */
export const DLS_SENSOR_EXPIRY_SECONDS = DEVICE_DATA_WINDOW_DAYS * 24 * 60 * 60 + 6 * 60 * 60

/**
 * Whether a DLS sensor's wear life (14d + 6h grace, from its own
 * `devices.start_time` activation anchor) has passed as of `nowSeconds`.
 *
 * Fails OPEN (not expired) on a missing/invalid/non-positive `start_time` —
 * the same posture as ada-core's `isPastDlsSensorExpiry`: an unknown anchor
 * means "cannot evaluate", not "always expired" (vic/vetpo#63).
 */
export function isDlsSensorExpired(
  deviceStartTimeSeconds: number | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): boolean {
  if (typeof deviceStartTimeSeconds !== 'number' || !Number.isFinite(deviceStartTimeSeconds)) {
    return false
  }
  if (deviceStartTimeSeconds <= 0) return false
  return nowSeconds > deviceStartTimeSeconds + DLS_SENSOR_EXPIRY_SECONDS
}

/**
 * Raw (pre-calibration) glucose at or below which a DLS reading is the firmware
 * "no valid reading" placeholder.
 *
 * Must stay equal to ada-core's `DLS_GLUCOSE_SENTINEL_MAX_RAW_MMOL`
 * (src/collections/Metrics/sentinel.ts) — that server-side constant decides
 * `is_sentinel`, and this client-side one decides "at sensor minimum". They
 * describe the same physical condition, and until 2026-08-26 they disagreed:
 * the server worked backwards through each device's own calibration while the
 * client compared a STORED value against a DISPLAY constant (vetpo#2).
 */
export const DLS_GLUCOSE_SENTINEL_MAX_RAW = 0.1

export interface CGMReading {
  timestamp?: number
  glucose: number
  offset: number
  [key: string]: any
}

const convertReadings = (reading: number, unit = GLUCOSE_UNIT_MM) =>
  reading > 0 ? reading * (unit === GLUCOSE_UNIT_MG ? MMOL_TO_MGDL_FACTOR : 1) : 0

/**
 * Checks if a glucose reading is valid and should be included in charts
 * Invalid readings will cause gaps in trend lines
 */
export function isValidGlucoseReading(reading: CGMReading): boolean {
  // Check for missing timestamp
  if (reading.timestamp === undefined || reading.timestamp === null) {
    return false
  }

  // Check for invalid glucose values
  if (
    reading.glucose === null ||
    reading.glucose === undefined ||
    Number.isNaN(reading.glucose) ||
    !Number.isFinite(reading.glucose)
  ) {
    return false
  }

  return true
}

export const DLS_BGM_CORRECTION = {
  type: 'power' as const,
  a: 1.46286,
  b: 0.7971,
  version: '2026-08-07',
}

/**
 * QR → BGM display correction: y = min(x, a·x^b).
 *
 * The identity cap (2026-08-07) is the low-end fix from ada-core
 * docs/GLUCOSE_LOW_RANGE_FLOOR.md §3.2: the power fit has ZERO paired data
 * below QR 4.99 yet was being applied down to the sensor floor at QR 3.0,
 * where its extrapolated lift (+17%) contributed 9.2 mg/dL of the 63 mg/dL
 * display floor. Below the crossover at QR 6.52 (where a·x^b crosses y=x)
 * the correction now passes values through unchanged; above it nothing
 * changes (min picks the power branch there). Evaluated on the fit's own
 * training data the capped form is equal-or-better (MARD 16.82%→16.73%,
 * low-band bias +0.103→−0.009) — see AdapetApp
 * docs/bgm_identity_low_end_comparison.png.
 *
 * Keep in lockstep with AdapetApp common.js (+ its iOS/Android sidecars),
 * ops/web glucose.ts, and BOTH Go ports (alert-engine, ops monitoring) —
 * gated by ada-core go-services/scripts/check-bgm-consistency.sh.
 */
export function applyDLSBGMCorrection(calibratedValue: number): number {
  if (typeof calibratedValue !== 'number' || Number.isNaN(calibratedValue)) return calibratedValue
  const cor = DLS_BGM_CORRECTION
  if (calibratedValue <= 0) return 0
  return Math.min(calibratedValue, cor.a * calibratedValue ** cor.b)
}

const DANGER_ZONE_K = 30
export function clampToDisplayRange(value: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value
  if (value <= 0) return GLUCOSE_VALID_RANGE.min
  if (value < GLUCOSE_VALID_RANGE.min) return GLUCOSE_VALID_RANGE.min
  if (value <= 30) return value
  return 40 - 10 * Math.exp(-(value - 30) / DANGER_ZONE_K)
}

/**
 * Full display transform for a stored (QR-calibrated) glucose value:
 * BGM correction, then the display-range clamp.
 *
 * This is the transform every EXPORT must use (CSV from PetShow, CSV from the
 * Metrics list) so an exported number is the same number the vet reads on
 * screen. Mirrors AdapetApp's `applyDisplayCorrection` in
 * `src/utils/glucoseUtils.js` — keep the two in sync.
 *
 * The clamp is not cosmetic. Without it an export emits values the sensor
 * cannot support: below 2.2 mmol/L (40 mg/dL) is under the sensor's validated
 * floor, and the raw BGM curve is unbounded above, so a saturated reading
 * exported as e.g. 57 mmol/L is false precision, not data. Consequence to know:
 * corrected values under 2.2 floor AT 2.2, so a severe-hypo row exports as
 * "40 mg/dL" rather than a smaller number — which is exactly what the UI shows
 * for it ("Out of range low", see GLUCOSE_OUT_OF_RANGE_LOW).
 *
 * NOT identical to the chart pipeline: `normalizeGlucoseReadingForChart` also
 * pre-clamps the RAW value to GLUCOSE_DISPLAY_CLAMP_MAX (50) before correcting,
 * so that a saturated sensor lands past the 30 mmol/L out-of-range threshold
 * and hits the Y-axis cap. That pre-clamp is a charting concern; exports keep
 * the corrected value. The two therefore differ only for stored values above
 * 50 mmol/L, all of which display as "Out of range high" either way.
 */
/**
 * ⚠️ NOT for exports or for anything that then formats/masks the value.
 * Use `formatGlucoseForExport` (below) instead.
 *
 * This returns a CLAMPED number, and the clamp floors to 2.2 — the exact value
 * `GLUCOSE_OUT_OF_RANGE_LOW` holds. That USED to kill the "<40 mg/dL" mask,
 * because the low test was strict (`<`) and a floored drop-out read as a
 * genuine 40. Since 2026-08-08 the low test is INCLUSIVE (`<=`), so a clamped
 * floor value still masks and no caller has to remember the ordering; see
 * src/__tests__/glucoseOutOfRangeMask.regression.test.ts, which pins it.
 *
 * It currently has NO caller in src/; it is kept
 * as the numeric mirror of AdapetApp's `applyDisplayCorrection`
 * (src/utils/glucoseUtils.js) for anyone who genuinely needs a plottable,
 * in-band NUMBER (as the chart pipeline does via
 * `normalizeGlucoseReadingForChart`).
 */
export function applyDisplayCorrection(calibratedValue: number): number {
  if (typeof calibratedValue !== 'number' || !Number.isFinite(calibratedValue)) {
    return calibratedValue
  }
  return clampToDisplayRange(applyDLSBGMCorrection(calibratedValue))
}

/**
 * The CSV cell for a stored (QR-calibrated) glucose value — the EXPORT
 * equivalent of what `formatGlucoseForDisplay` puts on screen, without the unit
 * suffix (exports state the unit in the filename and column header).
 *
 * Deliberately does NOT clamp — an export should carry the reading and its
 * bounds, not a display-compressed number. The reason changed on 2026-08-08;
 * the detail below is the current one.
 *
 * `clampToDisplayRange` floors to `GLUCOSE_VALID_RANGE.min` (2.2), the SAME
 * constant as `GLUCOSE_OUT_OF_RANGE_LOW`. When the mask tested `< 2.2`
 * STRICTLY, clamping first pinned the value to exactly 2.2 and the "<2.2"
 * branch could never fire, so a sub-floor row exported as a bare "40 mg/dL",
 * indistinguishable from a genuine 40. `6d2f4fc1` fixed that at the source by
 * making the low bound INCLUSIVE (`<= 2.2`) in both `isGlucoseOutOfRange` and
 * `formatGlucoseForDisplay`, so a clamped drop-out masks correctly now.
 *
 * Skipping the clamp is therefore no longer what keeps the mask alive — the
 * `<=` is. It is kept because it makes this formatter INDEPENDENT of the
 * clamp: the masked output is identical either way today (verified
 * 2026-08-29), and it stays correct even if the clamp's floor moves.
 *
 * The original defect was not hypothetical: on 2026-08-02, 239 rows in 7 days
 * across six ACTIVELY reporting prod devices were sparse sensor drop-outs
 * (stored 0.01-1.6) — too infrequent for dislodgement detection to hide, since
 * that needs 90% of a 60-reading window. The file says "<40", exactly as the
 * screen already does.
 *
 * In-band values are unchanged: the clamp is identity on [2.2, 30].
 */
export function formatGlucoseForExport(storedValue: number, unit: string): string {
  if (typeof storedValue !== 'number' || !Number.isFinite(storedValue)) return ''
  return formatGlucoseForDisplay(applyDLSBGMCorrection(storedValue), unit, { withUnit: false })
}

/**
 * Normalize a reading for chart rendering:
 * - missing/invalid values => null (render as gap)
 * - numeric values => BGM-corrected then clipped to valid bounds
 */
/**
 * The standard chart-series pipeline, in one place: BGM-correct + clamp each
 * reading (normalizeGlucoseReadingForChart — invalid readings drop out and
 * render as gaps), then the 5-point trailing smooth (smoothCGM). Five chart
 * surfaces and the PDF capture used to inline this exact pair; keeping copies
 * identical by convention is how pipelines drift (the 2026-04-13 TIR-mismatch
 * incident was two of these disagreeing).
 *
 * Order-dependent: smoothCGM is a trailing average, so pass readings sorted
 * ascending by timestamp (every list query in the app already does).
 */
export function prepareGlucoseChartSeries(readings: CGMReading[]): CGMReading[] {
  return smoothCGM(
    readings.map(normalizeGlucoseReadingForChart).filter((r): r is CGMReading => r !== null),
  )
}

export function normalizeGlucoseReadingForChart<T extends CGMReading>(reading: T): T | null {
  if (!isValidGlucoseReading(reading)) {
    return null
  }
  return {
    ...reading,
    // Clamp raw to the display ceiling (50) BEFORE the BGM correction so a
    // saturated reading maps past the out-of-range threshold and reaches the
    // 540 cap. clampToDisplayRange then bounds the corrected value.
    glucose: clampToDisplayRange(
      applyDLSBGMCorrection(Math.min(reading.glucose, GLUCOSE_DISPLAY_CLAMP_MAX)),
    ),
  }
}

/**
 * Clamp glucose into the DISPLAY range ([MIN, DISPLAY_CLAMP_MAX] = [2.2, 50] mmol/L).
 * Single source for display-pipeline clamping (mirrors AdapetApp).
 */
export function clampGlucoseToDisplayRange(value: number, unit = GLUCOSE_UNIT_MM): number {
  return unit === GLUCOSE_UNIT_MM
    ? Math.max(Math.min(GLUCOSE_DISPLAY_CLAMP_MAX, value), GLUCOSE_VALID_RANGE.min)
    : Math.max(
        Math.min(convertReadings(GLUCOSE_DISPLAY_CLAMP_MAX, GLUCOSE_UNIT_MG), value),
        convertReadings(GLUCOSE_VALID_RANGE.min, GLUCOSE_UNIT_MG),
      )
}

export function clip(g: number, unit = GLUCOSE_UNIT_MM) {
  return clampGlucoseToDisplayRange(g, unit)
}
/**
 * Smooths raw CGM readings with a 5-point TRAILING simple moving average, by
 * delegating to `smoothCGMMA` (`smoothCGMMedian` and a raw pass-through are
 * kept below as commented alternatives).
 *
 * Bounds: every reading is `clip`ped before averaging and every average is
 * clipped again, so output lands in
 * [GLUCOSE_VALID_RANGE.min, GLUCOSE_DISPLAY_CLAMP_MAX] = [2.2, 50] mmol/L —
 * NOT [2.2, 40]. `clip` delegates to `clampGlucoseToDisplayRange`, whose upper
 * bound is the DISPLAY clamp (valid max + 10), not `GLUCOSE_VALID_RANGE.max`.
 *
 * The window is over ARRAY POSITION, not time. Gaps are NOT interpolated: a
 * trailing window averages straight across a multi-hour gap as though the
 * readings were contiguous. Callers must therefore pass readings sorted
 * ASCENDING by timestamp (see `prepareGlucoseChartSeries`); deciding where a
 * line BREAKS for a gap is a separate concern owned by
 * `buildGlucoseLineSegments`.
 *
 * With fewer than `horizon` (5) readings the input is returned clipped but
 * unsmoothed.
 *
 * @param rawReadings readings sorted ascending by timestamp
 * @returns a new array; the input is not mutated
 */
export function smoothCGM(
  rawReadings: CGMReading[],
  // unit = GLUCOSE_UNIT_MM,
) {
  // return rawReadings;
  // return smoothCGMMedian(rawReadings);
  return smoothCGMMA(rawReadings)
}

export function smoothCGMMedian(
  rawReadings: CGMReading[],
  // unit = GLUCOSE_UNIT_MM,
) {
  return rawReadings.map((curr, idx, arr) => {
    // idx === 0 has no “previous,” so just return the current
    if (idx === 0) {
      return { ...curr, glucose: clip(curr.glucose) }
    }

    // grab the previous reading
    const prev = arr[idx - 1]

    // now you can do whatever you want with prev.glucose
    const alpha = 0.5
    return {
      ...curr,
      glucose: clip(prev.glucose * (1 - alpha) + curr.glucose * alpha),
    }
  })
}

export function smoothCGMMA(
  rawReadings: CGMReading[],
  horizon = 5,
  // unit = GLUCOSE_UNIT_MM,
) {
  // Create a clipped copy of glucose values
  const clipped = rawReadings.map((r) => ({ ...r, glucose: clip(r.glucose) }))

  // If fewer readings than horizon, return clipped values
  if (clipped.length < horizon) {
    return clipped
  }

  // Apply N-point moving average smoothing with dynamic window size
  const smoothed: CGMReading[] = []
  for (let i = 0; i < clipped.length; i++) {
    const start = Math.max(0, i - horizon + 1)
    const window = clipped.slice(start, i + 1)
    const sum = window.reduce((acc, r) => acc + r.glucose, 0)
    const avg = sum / window.length
    smoothed.push({ ...clipped[i], glucose: clip(avg) })
  }

  return smoothed
}
